/**
 * Shared EN/中文 language for the whole drama feature, persisted to localStorage
 * and reactive across routes (detail page toggle ↔ project list ↔ dialogs) via
 * useSyncExternalStore.
 */
import { useSyncExternalStore } from 'react'

import type { DramaLang } from '@/types/drama'

const KEY = 'drama.lang'

function read(): DramaLang {
  try {
    const v = localStorage.getItem(KEY)
    if (v === 'en' || v === 'zh') return v
  } catch {
    /* ignore */
  }
  return 'zh'
}

let current: DramaLang = read()
const listeners = new Set<() => void>()

export function setDramaLang(lang: DramaLang): void {
  current = lang
  try {
    localStorage.setItem(KEY, lang)
  } catch {
    /* ignore */
  }
  listeners.forEach((fn) => fn())
}

export function useDramaLang(): [DramaLang, (lang: DramaLang) => void] {
  const lang = useSyncExternalStore(
    (cb) => {
      listeners.add(cb)
      return () => listeners.delete(cb)
    },
    () => current,
    () => current,
  )
  return [lang, setDramaLang]
}
